import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlus, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { fetchPosts } from '../../../api/post';
import styles from './Content.module.css';
import Pagination from './Pagination';
import PostRow from './PostRow';

const Content = () => {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const pageSize = 10;

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchPosts(currentPage, pageSize, 'all');
      if (response.data.code === 200) {
        const { content = [], totalElements = 0, totalPages: pages = 0 } = response.data.data;
        setPosts(content);
        setTotalPosts(totalElements);
        setTotalPages(pages);
      } else {
        setError(response.data.message || 'Unable to load articles.');
      }
    } catch {
      setError('The article API is unavailable. Check that the backend is running.');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const visiblePosts = useMemo(() => posts.filter((post) =>
    `${post.title} ${post.author || ''}`.toLowerCase().includes(query.toLowerCase())
  ), [posts, query]);

  return (
    <div>
      <header className={styles.pageHeader}>
        <div>
          <p>WRITING</p>
          <h1>Articles</h1>
          <span>{totalPosts} article{totalPosts === 1 ? '' : 's'} across all languages.</span>
        </div>
        <Link to="/admin/posts/new" className={styles.primaryButton}><FiPlus /> New article</Link>
      </header>

      <div className={styles.toolbar}>
        <label className={styles.search}><FiSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search this page" /></label>
        <button type="button" onClick={loadPosts}>Refresh</button>
      </div>

      <div className={styles.tableCard}>
        {loading && <div className={styles.state}>Loading articles…</div>}
        {!loading && error && <div className={`${styles.state} ${styles.error}`}>{error}</div>}
        {!loading && !error && !visiblePosts.length && <div className={styles.state}>No articles found.</div>}
        {!loading && !error && visiblePosts.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead><tr><th>Article</th><th>Author</th><th>Language</th><th>Published</th><th><span className={styles.visuallyHidden}>Actions</span></th></tr></thead>
              <tbody>{visiblePosts.map((post) => <PostRow key={post.id} post={post} onDeleted={loadPosts} />)}</tbody>
            </table>
          </div>
        )}
      </div>

      {!error && totalPages > 1 && <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} totalPages={totalPages} />}
    </div>
  );
};

export default Content;
